import { createSlice } from '@reduxjs/toolkit';
import AbstractSliceHandler from 'modules/shared/stores/abstract-slice-handler';
import LocalEvent from 'utils/local-event';
import Log from 'utils/log';
import sdkClient from 'utils/sdk-client';
import { randUuid, execBackgroundTask, offsetFromPage } from 'utils/functions';
import { getCompanySettings, isLargeOrganization } from 'modules/settings/stores/company-settings-slice-handler';
import {
    ContactMenuOption,
    ContactMenuType,
    UserType,
    RobotType,
    CugContactType,
    SearchInputDefaultData,
    ALL_GROUP_ID,
} from 'constants/contacts-constants';
import {
    contactsAreEqual,
    createGroupStructure,
    createUserStructure,
    cugContactToSdkEntity,
    getLoggedInMsisdn,
    openUserGroupDialog,
    sdkEntityToCugContact,
    createEmptyContactsIdsList,
    contactsRecordsOnPage,
    getHumanDeviceOsList,
} from 'utils/contact-util';
import { addConfirmationDialog, addToaster } from '../../../utils/dialogs-manager';
import t from '../../../utils/translate';
import { TabTypes } from '../../../constants/user-groups-constants';
import { getUserSettingsJson } from '../../settings/stores/user-settings-slice-handler';
import Queue from '../../../utils/queue';
import { openGuestInvitationModal } from './guest-invitation-slice-handler';

const initialState = {
    /**
     * This member is keeping track of the search inputs which have been registered in the store.
     * Each search input will store under the state an object of type ContactsSearchInputData
     * {[key:string]: ContactsSearchInputData}
     */
    searchInputs: {},
    /**
     * This member is keeping track of the contacts selectors which have been registered in the store.
     * Each contact will store under the state an object of type ContactsSelectorData
     * {[key:string]: ContactsSelectorData}
     */
    contactsSelectors: {},
    userPictures: {},
    users: {},
    groups: {},
    /**
     * The ids of the groups where the current user is member.
     * @type {number}
     */
    groupsMembership: [],
    /**
     * The ids of the groups which can be managed by the current user.
     * @type {number}
     */
    groupsOwnership: [],
    /**
     * This list of the groups entities in the proper order which must be served to view.
     * The list is updated by all group updates.
     * @type {CugGroup[]}
     */
    sortedGroupsList: [],
    /**
     * The list of users sorted to be served in the Contacts page.
     * @type {CugSubscriber[]}
     */
    sortedUsersList: [],
    /**
     * The list of favorite contacts prepared to be used in a selector hook.
     * @type {ContactsSelectorData}
     */
    favoritesContactsData: {
        msisdns: [],
        groupsIds: [],
    },
    /**
     * The list of contacts which were displayed, but have been removed (by cUG update or manually)
     * @type {ContactsSelectorData}
     */
    removedContactsData: {
        msisdns: [],
        groupsIds: [],
    },
    favoritesGroupsList: [],
    /**
     * This member is used in the Contacts page and facilitates communication between the SideBar and MainArea
     * @type {ContactsPageData}
     */
    contactsPageData: {
        contactsSelectorUuid: null,
        globalAction: null,
        dataIsUpdating: false,
    },
    /**
     * The filters that are applied on the users list
     */
    usersListFilters: {
        Connectivity: [],
        UserAvailability: [],
        OperationalStatusIds: [],
    },
    /**
     * The details related with the PBX dialer (Call out)
     */
    pbxDialerData: {
        visible: false,
        // the current callback to be executed instead of the default behavior
        callback: null,
    },
    /**
     * The list of contacts for which the details are open.
     * Ex: open the details of a user => click over a displayed group => open the details of that group => click on a user memer => ...
     */
    detailsChain: [],
    /**
     * The index of the details chain which is currently displayed.
     */
    detailsIndex: 0,
    detailsRefreshing: false,
    /**
     * The contact for which the details are loading.
     * @type {CugContact|null}
     */
    detailsLoadingContact: null,
};

class ContactsSliceHandler extends AbstractSliceHandler {
    static getInstance() {
        if (!ContactsSliceHandler.instance) {
            ContactsSliceHandler.instance = new ContactsSliceHandler();
        }

        return ContactsSliceHandler.instance;
    }

    pendingUserDetailsRequests = {};

    pendingUserPicturesRequests = {};

    pendingGroupsDetailsRequests = {};

    constructor() {
        super('contactsSlice');

        LocalEvent.setListener(LocalEvent.eventTypes.SDK_CLIENT_AUTHENTICATED, (event) => {
            this.onSdkClientAuthenticated(event);
        });

        LocalEvent.setListener(LocalEvent.eventTypes.COMPANY_SETTINGS_UPDATED, () => {
            this.onCompanySettingsUpdated();
        });

        sdkClient.setSocketEventListener('ORGANIZATION_DATA_RESPONSE', (cugData) => this.parseCugData(cugData));
        sdkClient.setSocketEventListener('ORGANIZATION_DATA_UPDATE', (cugData) => this.parseCugData(cugData));
        sdkClient.setSocketEventListener('WCFUI_USER_PICTURE_RESPONSE', (pictureData) =>
            this.onUserPictureResponse(pictureData),
        );

        this.slice = createSlice({
            name: this.sliceName,
            initialState,
            reducers: {
                setUsersListFilters(state, action) {
                    state.usersListFilters = action.payload;
                },
                updateSearchInput(state, action) {
                    if (typeof state.searchInputs[action.payload.uuid] === 'undefined') {
                        state.searchInputs[action.payload.uuid] = action.payload.data;
                    } else {
                        state.searchInputs[action.payload.uuid] = {
                            ...state.searchInputs[action.payload.uuid],
                            ...action.payload.data,
                        };
                    }
                },
                removeSearchInput(state, action) {
                    delete state.searchInputs[action.payload];
                },
                updateContactsSelector(state, action) {
                    if (typeof state.contactsSelectors[action.payload.uuid] === 'undefined') {
                        state.contactsSelectors[action.payload.uuid] = action.payload.data;
                    } else {
                        state.contactsSelectors[action.payload.uuid] = {
                            ...state.contactsSelectors[action.payload.uuid],
                            ...action.payload.data,
                        };
                    }
                },
                removeContactsSelector(state, action) {
                    delete state.contactsSelectors[action.payload];
                },
                setUserPicture(state, action) {
                    state.userPictures[action.payload.msisdn] = {
                        ...(state.userPictures[action.payload.msisdn] ?? {}),
                        ...action.payload,
                    };
                },
                removeUserPicture(state, action) {
                    delete state.userPictures[action.payload];
                    // reset as well use user's picture timestamp, to not request it again
                    state.users[action.payload].PictureTimestamp = 0;
                },
                setUserData(state, action) {
                    ContactsSliceHandler.getInstance().slice.caseReducers.setMultipleUserData(state, {
                        payload: [action.payload],
                    });
                },
                /**
                 * This function is used to persist under store.
                 * The function may receive directly the list of users, or an OBJECT which has two properties:
                 * - users
                 * - ignoredProps = the properties which should NOT be inherited; it applies for the user is already cached
                 * @param state
                 * @param action
                 */
                setMultipleUserData(state, action) {
                    let users = [];
                    let ignoredProps = [];
                    const overide = {};

                    if (typeof action.payload.ignoredProps !== 'undefined') {
                        users = [...action.payload.users];
                        ignoredProps = action.payload.ignoredProps;
                    } else {
                        users = action.payload;
                    }

                    users.forEach((userData) => {
                        if (state.users[userData.Msisdn]) {
                            const cachedUser = state.users[userData.Msisdn];

                            // do not inherit the ignored props
                            ignoredProps.forEach((prop) => {
                                overide[prop] = cachedUser[prop];
                            });

                            // update only if is necessary
                            if (contactsAreEqual(cachedUser, userData, ['CugTimestamp'])) {
                                return;
                            }
                        }

                        state.users[userData.Msisdn] = {
                            ...(state.users[userData.Msisdn] ?? {}),
                            ...userData,
                            ...overide,
                        };
                        if (state.removedContactsData.msisdns.includes(userData.Msisdn)) {
                            state.removedContactsData.msisdns = state.removedContactsData.msisdns.filter(
                                (msisdn) => msisdn !== userData.Msisdn,
                            );
                        }
                    });

                    ContactsSliceHandler.getInstance().slice.caseReducers.computeSortedUsersListForContactsPage(state);
                },
                removeUserData(state, action) {
                    const msisdn = action.payload;
                    const newState = { ...state.users };
                    delete newState[msisdn];
                    state.users = { ...newState };

                    if (!state.removedContactsData.msisdns.includes(msisdn)) {
                        state.removedContactsData.msisdns = [...state.removedContactsData.msisdns, msisdn];
                    }
                },
                setGroupData(state, action) {
                    ContactsSliceHandler.getInstance().slice.caseReducers.setMultipleGroupData(state, {
                        payload: [action.payload],
                    });
                },
                setMultipleGroupData(state, action) {
                    let updatedItems = 0;
                    const newState = {};

                    action.payload.forEach((groupData) => {
                        // update only if is necessary
                        if (
                            state.groups[groupData.GroupId] &&
                            contactsAreEqual(state.groups[groupData.GroupId], groupData, [
                                'Member',
                                'Members',
                                'IsUserGroup',
                                'IsUserGroupAdmin',
                                'CugTimestamp',
                                'IsUpdating',
                            ])
                        ) {
                            return;
                        }

                        updatedItems++;

                        newState[groupData.GroupId] = { ...(state.groups[groupData.GroupId] ?? {}), ...groupData };
                        if (state.removedContactsData.groupsIds.includes(groupData.GroupId)) {
                            state.removedContactsData.groupsIds = state.removedContactsData.groupsIds.filter(
                                (groupId) => groupId !== groupData.GroupId,
                            );
                        }
                    });

                    if (updatedItems === 0) {
                        return;
                    }

                    state.groups = { ...state.groups, ...newState };

                    ContactsSliceHandler.getInstance().slice.caseReducers.computeSortedGroupsList(state);
                },
                removeGroupData(state, action) {
                    const groupId = action.payload;
                    const newState = { ...state.groups };
                    delete newState[groupId];
                    state.groups = { ...newState };

                    if (!state.removedContactsData.groupsIds.includes(groupId)) {
                        state.removedContactsData.groupsIds = [...state.removedContactsData.groupsIds, groupId];
                    }
                },
                setContactsPageData(state, action) {
                    state.contactsPageData = { ...state.contactsPageData, ...action.payload };
                },
                setPbxDialerData(state, action) {
                    state.pbxDialerData = { ...state.pbxDialerData, ...action.payload };
                },
                updateDetailsChain(state, action) {
                    state.detailsChain = [...action.payload];
                },
                updateDetailsIndex(state, action) {
                    state.detailsIndex = action.payload;
                },
                updateDetailsRefreshing(state, action) {
                    state.detailsRefreshing = action.payload;
                },
                setDetailsLoadingContact(state, action) {
                    state.detailsLoadingContact = action.payload;
                },
                setGroupsMembership(state, action) {
                    state.groupsMembership = action.payload;
                },
                setGroupsOwnership(state, action) {
                    state.groupsOwnership = action.payload;
                },
                // special "automatic" reducers which gets invoked for computed members
                computeSortedGroupsList(state) {
                    const favoriteGroups = {};
                    const favoritesGroupList = [];
                    const regularGroups = {};
                    const sortedGroups = [];

                    Object.keys(state.groups).forEach((groupId) => {
                        /**
                         * @type {CugGroup}
                         */
                        const group = state.groups[groupId];

                        if (group.IsFavorite) {
                            favoriteGroups[group.SearchKey] = group;
                            favoritesGroupList.push(group.GroupId);
                        } else {
                            regularGroups[group.SearchKey] = group;
                        }
                    });

                    Object.keys(favoriteGroups)
                        .sort()
                        .forEach((groupName) => {
                            sortedGroups.push(favoriteGroups[groupName].GroupId);
                        });

                    Object.keys(regularGroups)
                        .sort()
                        .forEach((groupName) => {
                            sortedGroups.push(regularGroups[groupName].GroupId);
                        });

                    state.sortedGroupsList = sortedGroups;
                    state.favoritesContactsData.groupsIds = favoritesGroupList;
                },
                computeSortedUsersListForContactsPage(state) {
                    const favoriteUsers = {};
                    const favoriteUsersList = [];
                    const regularUsers = {};
                    const sortedUsers = [];

                    Object.keys(state.users).forEach((msisdn) => {
                        /**
                         * @type {CugSubscriber}
                         */
                        const user = state.users[msisdn];
                        const userSortName = user.ComputedUserName.toLowerCase();

                        if (user.IsFavorite) {
                            favoriteUsers[userSortName] = user;
                            favoriteUsersList.push(user.Msisdn);
                        } else {
                            regularUsers[userSortName] = user;
                        }
                    });

                    Object.keys(favoriteUsers)
                        .sort()
                        .forEach((userName) => {
                            sortedUsers.push(favoriteUsers[userName].Msisdn);
                        });

                    Object.keys(regularUsers)
                        .sort()
                        .forEach((userName) => {
                            sortedUsers.push(regularUsers[userName].Msisdn);
                        });

                    state.sortedUsersList = sortedUsers;
                    state.favoritesContactsData.msisdns = favoriteUsersList;
                },
            },
        });
    }

    loadGroupsMembershipAndOwnership = () =>
        new Promise((resolve) => {
            const companySettings = getCompanySettings();

            const queue = new Queue(() => resolve());

            queue.add(() => {
                const startTime = new Date().getTime();
                Log.debug('CONTACTS STORE', `Requesting groups membership ...`);

                this.cugGetContact(getLoggedInMsisdn()).then((cugGetContact) => {
                    if (cugGetContact === null) {
                        return;
                    }

                    Log.debug(
                        'CONTACTS STORE',
                        `Loaded groups membership: ${cugGetContact.GroupsIds} in ${new Date().getTime() - startTime}ms.`,
                    );
                    this.dispatch('setGroupsMembership', cugGetContact.GroupsIds);

                    queue.advance();
                });
            });

            queue.add(() => {
                const startTime = new Date().getTime();
                Log.debug('CONTACTS STORE', `Requesting groups membership ...`);

                this.cugGetContact(getLoggedInMsisdn()).then((cugGetContact) => {
                    if (cugGetContact === null) {
                        return;
                    }

                    Log.debug(
                        'CONTACTS STORE',
                        `Loaded groups membership: ${cugGetContact.GroupsIds} in ${new Date().getTime() - startTime}ms.`,
                    );
                    this.dispatch('setGroupsMembership', cugGetContact.GroupsIds);
                    queue.advance();
                });
            });

            if (companySettings.AllowUserGroup) {
                queue.add(() => {
                    const startTime = new Date().getTime();
                    Log.debug('CONTACTS STORE', `Requesting groups ownership ...`);

                    webchatSDK.STWAPI.getUserGroupsRights().then((rights) => {
                        if (!rights || !rights.data) {
                            Log.error('CONTACTS STORE', `There was an error while loading groups ownership.`);
                            return;
                        }

                        Log.debug(
                            'CONTACTS STORE',
                            `Loaded groups ownership: ${rights.data.results} in ${new Date().getTime() - startTime}ms.`,
                        );
                        this.dispatch('setGroupsOwnership', rights.data.results);
                        queue.advance();
                    });
                });
            }

            queue.run();
        });

    /**
     * @return {CugSubscriber}
     */
    getLoggedInUser = () => {
        const state = this.getOwnState();

        if (typeof state.users[wcfUiCfg.msisdn] !== 'undefined') {
            return state.users[wcfUiCfg.msisdn];
        }

        return createUserStructure({
            Msisdn: wcfUiCfg.msisdn,
            UserConnectedStatus: true,
        });
    };

    /**
     * @param {CustomEvent} event
     */
    onSdkClientAuthenticated = (event) => {
        this.dispatch('setUserData', event.detail[0]);
    };

    onCompanySettingsUpdated = () => {
        const loggedInUser = {
            ...this.getLoggedInUser(),
            ...createUserStructure(getCompanySettings()),
        };

        this.dispatch('setUserData', loggedInUser);
    };

    setContactsFilters = (filters) => {
        this.dispatch('setUsersListFilters', filters);
    };

    /**
     * This is the only source of truth for contacts.
     * The function only does the searches, caches the results and resolves. Everything else, like next offset, ... must be handled outside
     * @param {'groups'|'subscribers'|'both'} entityType
     * @param {number} offset
     * @param {number} recordsOnPage
     * @param {ContactsSearchOptions} searchOptions
     * @return Promise<ContactsIdsList>
     */
    searchContacts = (entityType, offset, recordsOnPage, searchOptions = {}) => {
        const startTime = new Date().getTime();

        /**
         * @type {SdkContactsSearchOptions}
         */
        const sdkSearchOptions = {
            excludeDenyCommunicationGroups: true,
        };

        if (searchOptions.keyword) {
            sdkSearchOptions.keyword = searchOptions.keyword;
        }

        if (searchOptions.onlyHumans) {
            /**
             * @type {string[]}
             */
            sdkSearchOptions.deviceOs = getHumanDeviceOsList().join(',');
        }

        if (searchOptions.onlyFavorites) {
            sdkSearchOptions.onlyFavorites = searchOptions.onlyFavorites;
        } else {
            sdkSearchOptions.favoritesFirst = true;
        }

        if (entityType === webchatSDK.STWContactFilter.SINGLE && searchOptions.applyUsersListFilters === true) {
            /**
             * @type {UsersListFilters}
             */
            const filterData = this.getOwnState().usersListFilters;

            if (filterData.Connectivity.length === 1) {
                sdkSearchOptions.connectivity = filterData.Connectivity[0];
            }

            if (filterData.UserAvailability.length === 1) {
                sdkSearchOptions.userAvailability = filterData.UserAvailability[0];
            }

            if (filterData.OperationalStatusIds.length > 0) {
                sdkSearchOptions.operationalStatusIds = filterData.OperationalStatusIds.join(',');
            }
        }

        return new Promise((resolve) => {
            /**
             * @type {ContactsIdsList}
             */
            const result = createEmptyContactsIdsList();

            Log.debug(
                'CONTACTS STORE',
                `Performing SDK contacts search with entity type=${entityType}, ` +
                    `offset=${offset}, records=${recordsOnPage} and options=${JSON.stringify(sdkSearchOptions)}`,
            );

            webchatSDK.STWContactManager.searchContacts(entityType, offset, recordsOnPage, sdkSearchOptions, 1)
                .then((rawResults) => {
                    const totalTime = new Date().getTime() - startTime;
                    const users = [];
                    const groups = [];
                    rawResults.records.forEach((sdkEntity) => {
                        const cugContact = sdkEntityToCugContact(sdkEntity);

                        if (cugContact.IsGroup) {
                            // this cannot be favorite
                            if (searchOptions.onlyFavorites && cugContact.GroupId === ALL_GROUP_ID) {
                                return;
                            }

                            groups.push(cugContact);
                            result.groupsIds.push(cugContact.GroupId);
                        } else {
                            users.push(cugContact);
                            result.msisdns.push(cugContact.Msisdn);
                        }
                    });

                    Log.debug(
                        'CONTACTS STORE',
                        `Completed the search in ${totalTime}ms with ${result.msisdns.length + result.groupsIds.length} records returned.`,
                    );

                    if (users.length > 0) {
                        this.dispatch('setMultipleUserData', users);
                    }

                    if (groups.length > 0) {
                        this.dispatch('setMultipleGroupData', groups);
                    }

                    result.totalRecordsCount = rawResults.totalRecordsCount;

                    result.groupsIds = this.sortGroupsIdsList(result.groupsIds);
                    result.msisdns = this.sortMsisdnsList(result.msisdns);

                    resolve(result);
                })
                .catch((e) => {
                    Log.error(`CONTACTS STORE', 'An error has occurred while searching: ${e.message}`);
                    resolve(result);
                });
        });
    };

    /**
     * Get a list of users when there is available a list of msisns
     * @param {string[]} msisdnList
     * @return {Promise<ContactsIdsList>}
     */
    findUsersByMsisdnList = (msisdnList) =>
        new Promise((resolve) => {
            /**
             * @type {ContactsIdsList}
             */
            const result = createEmptyContactsIdsList();

            if (msisdnList.length === 0) {
                resolve(result);

                return;
            }

            const startTime = new Date().getTime();

            webchatSDK.STWAPI.findSubscribersByMsisdnList(msisdnList, false)
                .then((rawResults) => {
                    const totalTime = new Date().getTime() - startTime;
                    const users = [];

                    rawResults.data.results.forEach((rawUser) => {
                        const cugContact = createUserStructure(rawUser);

                        users.push(cugContact);
                        result.msisdns.push(cugContact.Msisdn);
                    });

                    Log.debug(
                        'CONTACTS STORE',
                        `Completed the FindByMsisdnList in ${totalTime}ms with ${result.msisdns.length} records returned.`,
                    );

                    if (users.length > 0) {
                        this.dispatch('setMultipleUserData', users);
                    }

                    result.totalRecordsCount = rawResults.totalRecordsCount;
                    result.msisdns = this.sortMsisdnsList(result.msisdns);

                    resolve(result);
                })
                .catch((e) => {
                    Log.error(`CONTACTS STORE', 'An error has occurred while finding by msisdn list: ${e.message}`);
                    resolve(result);
                });
        });

    /**
     * Get a list of groups when there is available a list of groups ids
     * @param {number[]} groupsIdsList
     */
    findGroupsByIdsList = (groupsIdsList) =>
        new Promise((resolve) => {
            /**
             * @type {ContactsIdsList}
             */
            const result = createEmptyContactsIdsList();

            if (groupsIdsList.length === 0) {
                resolve(result);

                return;
            }

            const startTime = new Date().getTime();

            const queue = new Queue(() => {
                const totalTime = new Date().getTime() - startTime;

                Log.debug(
                    'CONTACTS STORE',
                    `Completed the FindByGroupsIds in ${totalTime}ms with ${result.groupsIds.length} records returned.`,
                );

                result.totalRecordsCount = result.groupsIds.length;
                resolve(result);
            });

            groupsIdsList.forEach((groupId) => {
                queue.add(() =>
                    this.getGroupDetails(groupId).then((groupData) => {
                        if (groupData !== null) {
                            result.groupsIds.push(groupId);
                        }

                        queue.advance();
                    }),
                );
            });

            queue.run();
        });

    registerSearchInput = () => {
        const uuid = randUuid();

        this.dispatch('updateSearchInput', {
            uuid,
            data: SearchInputDefaultData,
        });

        Log.debug('CONTACTS STORE', `Registered a search input with id ${uuid}`);

        return uuid;
    };

    /**
     * Update the properties of a search input data.
     * @param {string} uuid
     * @param {ContactsSearchInputData} searchInputData
     */
    updateSearchInputData = (uuid, searchInputData) => {
        this.dispatch('updateSearchInput', {
            uuid,
            data: searchInputData,
        });
    };

    unregisterSearchInput = (uuid) => {
        Log.debug('CONTACTS STORE', `Unregistering search input with id ${uuid}`);

        this.dispatch('removeSearchInput', uuid);
    };

    /**
     * @param uuid
     * @return {ContactsSearchInputData}
     */
    getSearchInputData = (uuid) => this.getOwnState().searchInputs[uuid];

    /**
     * Request the given page in the search input.
     * @param {string} uuid
     * @param {number} page
     */
    requestSearchInputPage = (uuid, page) => {
        this.dispatch('updateSearchInput', {
            uuid,
            data: {
                page,
                requestInProgress: true,
                userIsTyping: false,
            },
        });

        /**
         * @type {ContactsSearchInputData}
         */
        const searchInputData = this.getOwnState().searchInputs[uuid];

        const recordsOnPage = searchInputData.recordsOnPage || contactsRecordsOnPage();
        const offset = offsetFromPage(page, recordsOnPage);

        this.searchContacts(searchInputData.contactType, offset, recordsOnPage, {
            keyword: searchInputData.keyword,
            onlyHumans: searchInputData.onlyHumans,
            applyUsersListFilters: searchInputData.applyUsersListFilters,
        }).then((data) => {
            this.dispatch('updateSearchInput', {
                uuid,
                data: {
                    requestInProgress: false,
                    matches: data,
                },
            });
        });
    };

    /**
     * Register a contacts selector.
     * @param {string|null} uuid It can be a pre-generated one or null. If is null will generate one.
     * @return {null}
     */
    registerContactsSelector = (uuid = null) => {
        if (!uuid) {
            uuid = randUuid();
        }

        const userSettings = getUserSettingsJson();

        this.dispatch('updateContactsSelector', {
            uuid,
            data: {
                msisdns: [],
                groupsIds: [],
                usersSearchInputId: this.registerSearchInput(),
                groupsSearchInputId: this.registerSearchInput(),
                usersPage: 1,
                groupsPage: 1,
                currentTab: userSettings.defaultContactsPageTab === 'users' ? TabTypes.USERS_TAB : TabTypes.GROUPS_TAB,
            },
        });

        Log.debug('CONTACTS STORE', `Registered a contacts selector with id ${uuid}`);

        return uuid;
    };

    /**
     * Check if the given id is registered or not.
     * @param {string} uuid
     */
    contactsSelectorIsRegistered = (uuid) => typeof this.getOwnState().contactsSelectors[uuid] !== 'undefined';

    /**
     * Update the properties of a contacts' selector.
     * @param {string} uuid
     * @param {ContactsSelectorData|null} contactsSelectorData
     */
    updateContactsSelectorData = (uuid, contactsSelectorData) => {
        this.dispatch('updateContactsSelector', {
            uuid,
            data: contactsSelectorData,
        });
    };

    resetContactsSelectorData = (uuid) =>
        this.updateContactsSelectorData(uuid, {
            msisdns: [],
            groupsIds: [],
            usersPage: 1,
            groupsPage: 1,
        });

    unregisterContactsSelector = (uuid) => {
        const selectors = this.getOwnState().contactsSelectors;

        if (typeof selectors[uuid] === 'undefined') {
            Log.debug('CONTACTS STORE', `Contacts selector with id ${uuid} was already unregistered.`);

            return;
        }

        Log.debug('CONTACTS STORE', `Unregistering contacts selector with id ${uuid}`);

        this.unregisterSearchInput(selectors[uuid].usersSearchInputId);
        this.unregisterSearchInput(selectors[uuid].groupsSearchInputId);

        this.dispatch('removeContactsSelector', uuid);
    };

    /**
     * @param uuid
     * @return {ContactsSelectorData}
     */
    getContactsSelectorData = (uuid) => this.getOwnState().contactsSelectors[uuid] || null;

    /**
     * Get the details about a user, when the msisdn is known.
     * @param {string} msisdn
     * @param {boolean} forceRequest Ignore the local state and force a request to server.
     * @return {Promise<CugSubscriber|null>}
     */
    getUserDetails = (msisdn, forceRequest = false) =>
        new Promise((resolve) => {
            const ownState = this.getOwnState();

            if (!forceRequest && typeof ownState.users[msisdn] !== 'undefined') {
                resolve(ownState.users[msisdn]);

                return;
            }

            webchatSDK.STWAPI.cugGetContact(msisdn)
                .then((userData) => {
                    if (!userData.data.results.ContactInfo || !userData.data.results.ContactInfo.Subscriber) {
                        // if the contact was somehow cached before, use it
                        if (typeof ownState.users[msisdn] !== 'undefined') {
                            resolve(ownState.users[msisdn]);
                        } else {
                            resolve(null);
                        }

                        return;
                    }

                    const user = createUserStructure(userData.data.results.ContactInfo.Subscriber[0]);

                    // cache the user details, but make sure that the core bugs do not affect existing structure
                    this.dispatch('setMultipleUserData', {
                        users: [user],
                        ignoredProps: ['IsFavorite'],
                    });

                    resolve(user);
                })
                .catch(
                    /**
                     * @param {SdkError} sdkError
                     */
                    (sdkError) => {
                        Log.warn('CONTACTS STORE', `There was an error loading user details: ${sdkError}`);

                        resolve(null);
                    },
                );
        });

    /**
     * Get the details about a user, when the msisdn is known.
     * @param {string} msisdn
     * @return {CugSubscriber|null|false} The function will return: CugSubscriber when user is found, null when user is not found,
     * and FALSE hwn the user is requested
     */
    getOrLoadUserDetails = (msisdn) => {
        const ownState = this.getOwnState();

        if (typeof ownState.users[msisdn] !== 'undefined') {
            return ownState.users[msisdn];
        }

        // user may have been removed
        if (ownState.removedContactsData.msisdns.includes(msisdn)) {
            return null;
        }

        // details request in progress => nothing is known yet for that msisdn
        if (typeof this.pendingUserDetailsRequests[msisdn] !== 'undefined') {
            return false;
        }

        // request the user details
        this.pendingUserDetailsRequests[msisdn] = true;

        Log.debug('CONTACTS STORE', `Requesting the details for user "${msisdn}"`);

        const startTime = new Date().getTime();

        this.getUserDetails(msisdn).then(() => {
            Log.debug('CONTACTS STORE', `Loaded details of  user "${msisdn}" in ${new Date().getTime() - startTime}ms`);

            delete this.pendingUserDetailsRequests[msisdn];
        });

        return false;
    };

    /**
     * Get a contact (user or group) when the id is known.
     * @param {string|number} contactId
     * @return {CugContact|null}
     */
    getContact = (contactId) => {
        const ownState = this.getOwnState();

        if (typeof ownState.users[contactId] !== 'undefined') {
            return ownState.users[contactId];
        }

        if (typeof ownState.groups[contactId] !== 'undefined') {
            return ownState.groups[contactId];
        }

        return null;
    };

    /**
     * Simple function which returns what it has in state, no request forced.
     * @param {string} msisdn
     * @return {CugSubscriber|null}
     */
    getUser = (msisdn) => {
        const ownState = this.getOwnState();

        return typeof ownState.users[msisdn] !== 'undefined' ? ownState.users[msisdn] : null;
    };

    /**
     * @param {CugData} cugData
     */
    parseCugData(cugData) {
        // the details about logged-in user may come with Cug data as well
        let loggedInUser = this.getLoggedInUser();

        let loggedInUserUpdated = false;
        let cumulatedCugSettings = {};

        if (typeof cugData.CugSettingsFrequent !== 'undefined') {
            cumulatedCugSettings = {
                ...cumulatedCugSettings,
                ...cugData.CugSettingsFrequent,
            };
            loggedInUserUpdated = true;
        }

        if (typeof cugData.CugSettingsRare !== 'undefined') {
            cumulatedCugSettings = {
                ...cumulatedCugSettings,
                ...cugData.CugSettingsRare,
            };
            loggedInUserUpdated = true;
        }

        if (loggedInUserUpdated) {
            loggedInUser = {
                ...loggedInUser,
                ...createUserStructure(cumulatedCugSettings),
            };

            this.dispatch('setUserData', loggedInUser);
        }

        if (typeof cugData.CugSubscribers !== 'undefined' || typeof cugData.CugGroups !== 'undefined') {
            Log.notice('CONTACTS STORE', `CUG update triggers groups additional data refreshing ...`);

            execBackgroundTask(() => {
                this.loadGroupsMembershipAndOwnership(getLoggedInMsisdn()).then(() => {
                    this.refreshAllContactsSelectors();
                    this.refreshDetailsChain();
                });
            });
        }

        // next code is functional, but is not added in the page because it may be expoensive in terms of performance
        // the code will be kept, for cases when there will be reported errors related with CUG not propagated
        /*
        if(typeof(cugData.CugSubscribers) !== 'undefined') {
            execBackgroundTask(() => this.refreshAllLoadedUsers());
        }

        if(typeof(cugData.CugGroups) !== 'undefined') {``
            execBackgroundTask(() => this.refreshAllLoadedGroups());
        }
         */

        // the next code is commented because the TECH department suggested to no longer use the CUG as reference
        // however, the code will be kept for history purpose
        /*
        if (typeof cugData.CugSubscribers !== 'undefined') {
            this.parseCugSubscribers(cugData);
        }

        if (typeof cugData.CugGroups !== 'undefined') {
            this.parseCugGroups(cugData);

            if (typeof cugData.CugGroupsMembership !== 'undefined') {
                this.parseCugGroupsMembership(cugData);
            } else {
                // this is linked with groups and must be always updated
                sdkClient.getCugEntities(['cug_groups_membership']).then((cugEntities) => {
                    if (typeof cugEntities.CugGroupsMembership !== 'undefined') {
                        this.parseCugGroupsMembership(cugEntities);
                    } else {
                        Log.error('CONTACTS STORE', `There was an error while loading CUG membership`);
                    }
                });
            }
        }
*/
    }

    /**
     * @deprecated CUG is no longer used
     * @param {CugData} cugData
     */
    parseCugSubscribers = (cugData) => {
        const ownState = this.getOwnState();
        /**
         * @type {CugSubscriber[]}
         */
        const ourUsers = [];
        const picturesToUpdate = [];

        const loadedUsers = Object.keys(ownState.users);
        const receivedMsisdns = [];

        cugData.CugSubscribers.Subscribers.forEach((cugSubscriber) => {
            ourUsers.push(createUserStructure(cugSubscriber));
            receivedMsisdns.push(cugSubscriber.Msisdn);

            // pictures management: remove those registered ones which no longer apply or update the registered ones if they changed
            // and ignore the rest; they will be loaded on demand
            if (
                // picture not yet loaded; the usr was not ye displayed => no need to load it now
                typeof ownState.userPictures[cugSubscriber.Msisdn] === 'undefined' ||
                // picture loaded locally, but its timestamp was not updated
                ownState.userPictures[cugSubscriber.Msisdn].PictureTimestamp === cugSubscriber.PictureTimestamp
            ) {
                return;
            }

            picturesToUpdate.push(cugSubscriber.Msisdn);
        });

        // persist the details in state
        this.dispatch('setMultipleUserData', ourUsers);

        // check for users that may have been deleted
        if (!isLargeOrganization()) {
            loadedUsers.forEach((msisdn) => {
                if (!receivedMsisdns.includes(msisdn) && msisdn !== getLoggedInMsisdn()) {
                    Log.notice(
                        'CONTACTS STORE',
                        `The user ${msisdn} was not found in last CUG, so will be removed from local cache.`,
                    );

                    this.dispatch('removeUserData', msisdn);
                }
            });
        }

        Log.debug(
            'CONTACTS STORE',
            `Processed ${cugData.CugSubscribers.Subscribers.length} users upon CUG message. There are ${picturesToUpdate.length} ` +
                `registered pictures that must be processed`,
        );

        if (picturesToUpdate.length === 0) {
            return;
        }

        execBackgroundTask(() => picturesToUpdate.forEach((msisdn) => this.getOrLoadUserPicture(msisdn)));
    };

    /**
     * @deprecated CUG is no longer used
     * @param {CugData} cugData
     */
    parseCugGroups = (cugData) => {
        const ownState = this.getOwnState();
        const ourGroups = [];
        const loadedGroups = Object.keys(ownState.groups).map((s) => parseInt(s, 10));
        const receivedGroups = [];

        cugData.CugGroups.Groups.forEach((group) => {
            ourGroups.push(createGroupStructure(group));

            receivedGroups.push(parseInt(group.GroupId, 10));
        });

        Log.debug('CONTACTS STORE', `Processed ${cugData.CugGroups.Groups.length} groups upon CUG message.`);

        this.dispatch('setMultipleGroupData', ourGroups);

        // check for users that may have been deleted
        loadedGroups.forEach((loadedGroupId) => {
            if (!receivedGroups.includes(loadedGroupId)) {
                Log.notice(
                    'CONTACTS STORE',
                    `The group ${loadedGroupId} was not found in last CUG, so will be removed from local cache.`,
                );

                this.dispatch('removeGroupData', loadedGroupId);
            }
        });
    };

    /**
     * @deprecated CUG is no longer used
     * @param {CugData} cugData
     */
    parseCugGroupsMembership = (cugData) => {
        const companySettings = getCompanySettings();

        Log.debug('CONTACTS STORE', `Updated groups membership: ${cugData.CugGroupsMembership.Groups}.`);

        if (companySettings.AllowUserGroup) {
            webchatSDK.STWAPI.getUserGroupsRights().then((rights) => {
                if (!rights || !rights.data) {
                    Log.error('CONTACTS STORE', `There was an error while loading groups ownership.`);

                    return;
                }

                Log.debug('CONTACTS STORE', `Updated groups ownership: ${rights.data.results}.`);
            });
        }
    };

    /**
     * Get the details about a group, when the group id is known.
     * @param {string} groupId
     * @return {CugGroup|null}
     */
    getOrLoadGroupDetails = (groupId) => {
        const ownState = this.getOwnState();

        if (typeof ownState.groups[groupId] !== 'undefined') {
            return ownState.groups[groupId];
        }

        // group may have been removed
        if (ownState.removedContactsData.groupsIds.includes(groupId)) {
            return null;
        }

        // details request in progress => nothing is known yet for that group id
        if (typeof this.pendingGroupsDetailsRequests[groupId] !== 'undefined') {
            return null;
        }

        // request the user details
        this.pendingGroupsDetailsRequests[groupId] = true;

        Log.debug('CONTACTS STORE', `Requesting the details for group "${groupId}"`);

        const startTime = new Date().getTime();

        this.getGroupDetails(groupId).then(() => {
            Log.debug(
                'CONTACTS STORE',
                `Loaded details of group "${groupId}" in ${new Date().getTime() - startTime}ms`,
            );

            delete this.pendingGroupsDetailsRequests[groupId];
        });

        return null;
    };

    /**
     * Request the details of a group.
     * @param {string} groupId
     * @param {boolean} includeChildGroups
     * @param {boolean} forceRequest Ignore the local cache and force a request
     * @return Promise<CugGroup|null>
     */
    getGroupDetails = (groupId, includeChildGroups = false, forceRequest = false) =>
        new Promise((resolve) => {
            const ownState = this.getOwnState();

            if (!forceRequest && typeof ownState.groups[groupId] !== 'undefined') {
                resolve(ownState.groups[groupId]);

                return;
            }

            webchatSDK.STWAPI.getGroupDetails(groupId, includeChildGroups)
                .then((res) => {
                    // group is out of User scope
                    if (!res || !res.data.results.ContactInfo.Group || res.data.results.ContactInfo.Group === null) {
                        this.dispatch('removeGroupData', groupId);
                        resolve(null);

                        return;
                    }

                    const cugGroup = createGroupStructure(res.data.results.ContactInfo.Group);
                    const groupsDataToPersist = [cugGroup, ...cugGroup.ChildGroups];
                    this.dispatch('setMultipleGroupData', groupsDataToPersist);

                    if (typeof cugGroup.Members) {
                        this.dispatch('setMultipleUserData', {
                            users: cugGroup.Members,
                            // seems core is badly returning these in group members
                            ignoredProps: ['IsFavorite', 'Groups'],
                        });
                    }

                    resolve(cugGroup);
                })
                .catch((reason) => {
                    Log.error(`There was an error while loading the details of group ${groupId}: ${reason}`);
                    resolve(null);
                });
        });

    /**
     * Simple function which returns what it has in state, no request forced.
     * @param {number|string} groupId
     * @return {CugGroup|null}
     */
    getGroup = (groupId) => {
        const ownState = this.getOwnState();

        // this may come from legacy; make use that we use always numbers
        groupId = parseInt(groupId, 10);

        return typeof ownState.groups[groupId] !== 'undefined' ? ownState.groups[groupId] : null;
    };

    /**
     * Check if certain group has the transmission denied.
     * @param {string} groupId
     * @return {boolean}
     */
    groupHasTransmissionDenied = (groupId) => {
        const state = this.getOwnState();

        /**
         * @type {CugGroup}
         */
        const group = state.groups[groupId];

        return group.GroupDenyTransmission && group.ExceptionDenyTransmission;
    };

    /**
     * Toggle the IsFavorite state of a contact.
     * @param {CugContact} readOnlyContact
     */
    toggleFavoriteStatus = (readOnlyContact) => {
        const contact = { ...readOnlyContact };

        const dispatchUpdateAction = (updatedContact) => {
            if (updatedContact.IsGroup) {
                this.dispatch('setGroupData', updatedContact);
            } else {
                this.dispatch('setUserData', updatedContact);
            }
        };

        contact.IsUpdating = true;
        dispatchUpdateAction(contact);

        setTimeout(() => {
            webchatSDK.STWContactManager.setFavorites([cugContactToSdkEntity(contact)], !contact.IsFavorite)
                .then(() => {
                    contact.IsFavorite = !contact.IsFavorite;
                    contact.IsUpdating = false;

                    dispatchUpdateAction(contact);
                })
                .catch((reason) => {
                    Log.error(
                        'CONTACTS STORE',
                        `There was an error while updating the favorite status of contact ${contact.ContactId}: ${reason}`,
                    );

                    contact.IsUpdating = false;
                    dispatchUpdateAction(contact);
                });
        });
    };

    /**
     * Add or remove a user in/from the contacts list.
     * @param {CugContact} readOnlyContact
     * @param {boolean} isContactState
     */
    toggleIsContactState = (readOnlyContact, isContactState) => {
        const contact = { ...readOnlyContact };

        const dispatchUpdateAction = (updatedContact) => {
            this.dispatch('setUserData', updatedContact);
        };

        contact.IsUpdating = true;
        dispatchUpdateAction(contact);

        setTimeout(() => {
            if (isContactState) {
                webchatSDK.STWContactManager.addToContactList(contact.Msisdn)
                    .then(() => {
                        contact.ContactType = CugContactType.MANUALLY_ADDED_CONTACT;
                        contact.IsUpdating = false;

                        dispatchUpdateAction(contact);
                    })
                    .catch((reason) => {
                        Log.error(
                            'CONTACTS STORE',
                            `There was an error while adding the contact ${contact.ContactId} in contacts list: ${reason}`,
                        );

                        contact.IsUpdating = false;
                        dispatchUpdateAction(contact);
                    });
            } else {
                webchatSDK.STWContactManager.removeFromContactList(contact.Msisdn)
                    .then(() => {
                        contact.ContactType = CugContactType.DEPARTMENT_CONTACT;
                        contact.IsUpdating = false;

                        dispatchUpdateAction(contact);
                    })
                    .catch((reason) => {
                        Log.error(
                            'CONTACTS STORE',
                            `There was an error while removing the contact ${contact.ContactId} from contacts list: ${reason}`,
                        );

                        contact.IsUpdating = false;
                        dispatchUpdateAction(contact);
                    });
            }
        });
    };

    /**
     * Check if the current user is a member of that group.
     * @param {number} groupId
     * @return {boolean}
     */
    groupIsMember = (groupId) => this.getOwnState().groupsMembership.includes(groupId);

    /**
     * Check if the current user can edit/delete that group.
     * @param {number} groupId
     * @return {boolean}
     */
    groupIsAdmin = (groupId) => this.getOwnState().groupsOwnership.includes(groupId);

    /**
     *
     * @param {CugContact|null} contact The contact for whom to get the menu option.
     * If is NULL then is about the GLOBAL menu. Ex: the one from the ContactsPage near to the search input
     * @param {ContactMenuType} menuType The type of the desired menu.
     * @return {string}
     */
    computeMenuOptions = (contact, menuType = ContactMenuType.CONTACTS_PAGE) => {
        const companySettings = getCompanySettings();
        const loggedInMsisdn = getLoggedInMsisdn();
        const options = [];

        if (contact === null) {
            // the GLOBAL menu (used for MULTI-SELECT)
            options.push(ContactMenuOption.BROADCAST_MESSAGE);

            if (companySettings.AllowVideoConference) {
                options.push(ContactMenuOption.RESTRICTED_ROOM);
            }

            if (companySettings.AllowBroadcastGroupCall) {
                options.push(ContactMenuOption.BROADCAST_CALL);
            }

            if (companySettings.AllowVideoStreaming && companySettings.ShareVideoStreaming) {
                options.push(ContactMenuOption.SHARE_SCREEN);
                options.push(ContactMenuOption.BROADCAST_VIDEO);
            }

            if (companySettings.AllowUserGroup) {
                options.push(ContactMenuOption.USER_GROUP_CREATE);
            }

            if (companySettings.AllowCallOut) {
                options.push(ContactMenuOption.DIALER);
            }

            if (companySettings.AllowVirality) {
                options.push(ContactMenuOption.INVITE_GUEST);
            }
        } else if (!contact.IsGroup) {
            // USER case
            do {
                if (contact.Msisdn !== loggedInMsisdn) {
                    if (contact.IsFavorite) {
                        options.push(ContactMenuOption.REMOVE_FROM_FAVORITES);
                    } else {
                        options.push(ContactMenuOption.ADD_TO_FAVORITES);
                    }
                }

                if ([UserType.ROBOT, UserType.CARTRACKER].includes(contact.Type)) {
                    options.push(ContactMenuOption.DETAILS);
                    // only Details available for non-human users
                    break;
                }

                if (contact.Type === UserType.EXTERNAL_VIDEO_SOURCE) {
                    options.push(ContactMenuOption.DETAILS);
                    if (companySettings.AllowVideoStreaming) {
                        options.push(ContactMenuOption.JOIN_BROADCAST_VIDEO);
                    }

                    // nothing more for external video sources
                    break;
                }

                if (contact.Type === UserType.EXTERNAL_VIDEO_DESTINATION) {
                    options.push(ContactMenuOption.DETAILS);
                    // nothing more for external video sources
                    break;
                }

                options.push(ContactMenuOption.DETAILS);
                options.push(ContactMenuOption.MESSAGE);

                if (contact.Msisdn === loggedInMsisdn) {
                    // not too many options for the logged-in user
                    break;
                }

                if (contact.Type === UserType.EXTERNAL_VIDEO_DESTINATION) {
                    // nothing more for external video destinations
                    break;
                }

                if (isLargeOrganization()) {
                    if (contact.ContactType === CugContactType.MANUALLY_ADDED_CONTACT) {
                        options.push(ContactMenuOption.REMOVE_FROM_CONTACTS);
                    } else {
                        options.push(ContactMenuOption.ADD_TO_CONTACTS);
                    }
                }

                if (companySettings.AllowVoip) {
                    options.push(ContactMenuOption.VOIP_CALL);

                    if (companySettings.AllowVoipVideo) {
                        options.push(ContactMenuOption.VIDEO_CALL);
                    }
                }

                if (companySettings.AllowWalkieTalkie) {
                    options.push(ContactMenuOption.PTT_CALL);
                }

                if (companySettings.AllowCallOut) {
                    options.push(ContactMenuOption.PBX_CALL);
                }

                if (companySettings.AllowVideoStreaming && companySettings.ShareVideoStreaming) {
                    options.push(ContactMenuOption.SHARE_SCREEN);
                    options.push(ContactMenuOption.BROADCAST_VIDEO);
                }

                if (companySettings.AllowMeetingRoom) {
                    options.push(ContactMenuOption.RESTRICTED_ROOM);
                }

                if (companySettings.AllowMyBusiness) {
                    options.push(ContactMenuOption.MY_BUSINESS_PROCESSES_LIST);
                }

                if (menuType === ContactMenuType.GEOLOCATION_PAGE && companySettings.IsDispatcher) {
                    options.push(ContactMenuOption.GEOLOCATION_REPORTS);
                }

                if (companySettings.AllowVideoConference) {
                    options.push(ContactMenuOption.RESTRICTED_ROOM);
                }
            } while (false);
        } else {
            options.push(ContactMenuOption.DETAILS);

            // GROUP case
            const transmissionDenied =
                contact.GroupDenyCommunication || this.groupHasTransmissionDenied(contact.GroupId);

            if (!transmissionDenied) {
                options.push(ContactMenuOption.MESSAGE);

                if (companySettings.AllowWalkieTalkie) {
                    options.push(ContactMenuOption.PTT_CALL);
                }

                if (companySettings.AllowVideoStreaming && companySettings.ShareVideoStreaming) {
                    options.push(ContactMenuOption.SHARE_SCREEN);
                    options.push(ContactMenuOption.BROADCAST_VIDEO);
                }

                if (companySettings.AllowVideoConference) {
                    options.push(ContactMenuOption.RESTRICTED_ROOM);
                }

                if (companySettings.AllowAudioConference) {
                    options.push(ContactMenuOption.CONFERENCE_CALL);
                }

                if (companySettings.AllowBroadcastGroupCall) {
                    options.push(ContactMenuOption.BROADCAST_CALL);
                }
            }
            if (contact.GroupId !== ALL_GROUP_ID) {
                if (contact.IsFavorite) {
                    options.push(ContactMenuOption.REMOVE_FROM_FAVORITES);
                } else {
                    options.push(ContactMenuOption.ADD_TO_FAVORITES);
                }
            }
            if (this.groupIsAdmin(contact.GroupId)) {
                options.push(ContactMenuOption.USER_GROUP_EDIT);
                options.push(ContactMenuOption.USER_GROUP_DELETE);
            }
        }

        // the , are necessary to check the full name
        return `,${options.join(',')},`;
    };

    /**
     * Trigger a menu option for a contact.
     * @param {CugContact} contact The contact for which the menu action is triggered.
     * @param {ContactMenuOption} menuOption
     * @param {object|null} additionalParams Additional parameters to be shipped with the local event.
     */
    triggerMenuOption = (contact, menuOption, additionalParams = null) => {
        const ownState = this.getOwnState();

        Log.debug('CONTACTS STORE', `Menu action ${menuOption} has been called for contact ${contact.ContactId}`);

        switch (menuOption) {
            case ContactMenuOption.DETAILS:
                this.openContactDetails(contact.ContactId, false);
                break;

            case ContactMenuOption.ADD_TO_FAVORITES:
            case ContactMenuOption.REMOVE_FROM_FAVORITES:
                this.toggleFavoriteStatus(contact);
                break;

            case ContactMenuOption.ADD_TO_CONTACTS:
                this.toggleIsContactState(contact, true);
                break;

            case ContactMenuOption.REMOVE_FROM_CONTACTS:
                this.toggleIsContactState(contact, false);
                break;

            case ContactMenuOption.USER_GROUP_EDIT:
                openUserGroupDialog([], contact.GroupId);
                break;

            case ContactMenuOption.USER_GROUP_DELETE:
                addConfirmationDialog({
                    question: t('js_all_contacts_user_group_delete_from_contacts'),
                    yesCallback: () => {
                        webchatSDK.STWContactManager.deleteUserGroup(cugContactToSdkEntity(contact))
                            .then(() => this.dispatch('removeGroupData', contact.ContactId))
                            .catch((e) => {
                                this.treatSdkError('userGroupDelete', e);
                            });
                    },
                    noCallback: () => false,
                    closeCallback: () => false,
                });
                break;

            case ContactMenuOption.MESSAGE:
                if (contact.IsGroup) {
                    // this is a weird-ish case when, normally should hve been sent a message in the PREARRANGED conversation with that group,
                    // but on SAAS it leads to the group conversation create.
                    // will do here as well
                    this.updateContactsPageData({
                        globalAction: ContactMenuOption.GROUP_CONVERSATION,
                    });
                    this.updateContactsSelectorData(ownState.contactsPageData.contactsSelectorUuid, {
                        groupsIds: [contact.GroupId],
                    });
                    // intentionally added the break under the if => to let to continue for user
                    break;
                }

            default:
                LocalEvent.trigger(LocalEvent.eventTypes.SINGLE_CONTACT_ACTION_TRIGGERED, {
                    action: menuOption,
                    contact,
                    additionalParams,
                });
                break;
        }
    };

    /**
     * Execute the action of a Contacts Selector Client
     * @param {string} contactsSelectorUuid The id of the contacts selector who made the selection.
     * @param {ContactMenuOption} action The client's action
     * @param {object|null} additionalParams Additional params required by ceryain actions.
     * @return {Promise<unknown>}
     */
    execContactsClientAction = (contactsSelectorUuid, action, additionalParams) => {
        const state = this.getOwnState();
        const contactsSelectorData = state.contactsSelectors[contactsSelectorUuid];
        const favoritesData = state.favoritesContactsData;

        const updatedUsers = [];
        const updateGroups = [];

        const sdkToRemoveFromFavorites = [];
        const sdkToAddToFavorites = [];
        const sdkPromises = [];

        /**
         * @type {ContactsSelectorData}
         */
        let selectionData = { ...this.getOwnState().contactsSelectors[contactsSelectorUuid] };

        const userGroupActionPreselectedContacts = [];

        switch (action) {
            // this is Add/Remove from favorites in fact
            case ContactMenuOption.ADD_TO_FAVORITES:
                this.validateContactsSelectorAction(contactsSelectorUuid, action).then((validationResult) => {
                    if (!validationResult) {
                        return;
                    }

                    // remove users
                    favoritesData.msisdns.forEach((msisdn) => {
                        if (!contactsSelectorData.msisdns.includes(msisdn)) {
                            updatedUsers.push({ ...state.users[msisdn], IsFavorite: false });
                            sdkToRemoveFromFavorites.push(cugContactToSdkEntity(state.users[msisdn]));
                        }
                    });

                    // add users
                    contactsSelectorData.msisdns.forEach((msisdn) => {
                        if (!favoritesData.msisdns.includes(msisdn)) {
                            updatedUsers.push({ ...state.users[msisdn], IsFavorite: true });
                            sdkToAddToFavorites.push(cugContactToSdkEntity(state.users[msisdn]));
                        }
                    });

                    // remove groups
                    favoritesData.groupsIds.forEach((groupId) => {
                        if (!contactsSelectorData.groupsIds.includes(groupId)) {
                            updateGroups.push({ ...state.groups[groupId], IsFavorite: false });
                            sdkToRemoveFromFavorites.push(cugContactToSdkEntity(state.groups[groupId]));
                        }
                    });

                    // add groups
                    contactsSelectorData.groupsIds.forEach((groupId) => {
                        if (groupId === ALL_GROUP_ID) {
                            // Skip adding ALL_GROUP_ID to favorites
                        } else if (!favoritesData.groupsIds.includes(groupId)) {
                            updateGroups.push({ ...state.groups[groupId], IsFavorite: true });
                            sdkToAddToFavorites.push(cugContactToSdkEntity(state.groups[groupId]));
                        } else {
                            // nothing to do
                        }
                    });

                    if (sdkToRemoveFromFavorites.length > 0) {
                        sdkPromises.push(webchatSDK.STWContactManager.setFavorites(sdkToRemoveFromFavorites, false));
                    }

                    if (sdkToAddToFavorites.length > 0) {
                        sdkPromises.push(webchatSDK.STWContactManager.setFavorites(sdkToAddToFavorites, true));
                    }

                    this.updateContactsPageData({ dataIsUpdating: true });

                    Promise.all(sdkPromises)
                        .then(() => {
                            if (updatedUsers.length > 0) {
                                this.dispatch('setMultipleUserData', updatedUsers);
                            }

                            if (updateGroups.length > 0) {
                                this.dispatch('setMultipleGroupData', updateGroups);
                            }

                            this.resetContactsSelectorData(contactsSelectorUuid);
                            this.updateContactsPageData({ globalAction: null });
                        })
                        .finally(() => {
                            this.updateContactsPageData({ dataIsUpdating: false });
                        });
                });
                break;

            case ContactMenuOption.USER_GROUP_CREATE:
                selectionData.msisdns.forEach((msisdn) => {
                    this.getUserDetails(msisdn).then((user) => {
                        userGroupActionPreselectedContacts.push(user);
                    });
                });

                selectionData.groupsIds.forEach((groupId) => {
                    this.getGroupDetails(groupId).then((group) => {
                        userGroupActionPreselectedContacts.push(group);
                    });
                });

                this.resetContactsSelectorData(contactsSelectorUuid);
                this.updateContactsPageData({ globalAction: null });

                setTimeout(() => {
                    openUserGroupDialog(userGroupActionPreselectedContacts, 0);
                });

                break;

            default:
                // validate the global action
                this.validateContactsSelectorAction(contactsSelectorUuid, action).then((validationResult) => {
                    if (!validationResult) {
                        return;
                    }

                    selectionData = { ...this.getOwnState().contactsSelectors[contactsSelectorUuid] };

                    const eventParam = {
                        action,
                        contacts: {
                            users: [],
                            groups: [],
                        },
                    };

                    selectionData.msisdns.forEach((msisdn) => {
                        this.getUserDetails(msisdn).then((user) => {
                            eventParam.contacts.users.push(user);
                        });
                    });

                    selectionData.groupsIds.forEach((groupId) => {
                        this.getGroupDetails(groupId).then((group) => {
                            eventParam.contacts.groups.push(group);
                        });
                    });

                    eventParam.additionalParams = additionalParams;

                    this.resetContactsSelectorData(contactsSelectorUuid);
                    this.updateContactsPageData({ globalAction: null });

                    setTimeout(() => {
                        LocalEvent.trigger(LocalEvent.eventTypes.MULTIPLE_CONTACTS_ACTION_TRIGGERED, eventParam);
                    });
                });
                break;
        }
    };

    /**
     * Validate the action which should be called against a list of selected contacts.
     * @param {string} contactsSelectorUuid The id of the contacts selector who made the selection.
     * @param {ContactMenuOption} action
     * @return {Promise<unknown>}
     */
    validateContactsSelectorAction = (contactsSelectorUuid, action) =>
        new Promise((resolve) => {
            const data = this.getOwnState().contactsSelectors[contactsSelectorUuid];

            const badUsers = {};
            const badGroups = {};
            let errorToasterShown = false;

            const transmissionRequiredActions = [
                ContactMenuOption.GROUP_CONVERSATION,
                ContactMenuOption.PTT_CALL,
                ContactMenuOption.BROADCAST_MESSAGE,
                ContactMenuOption.BROADCAST_CALL,
                ContactMenuOption.CONFERENCE_CALL,
                ContactMenuOption.SHARE_SCREEN,
                ContactMenuOption.BROADCAST_VIDEO,
            ];

            data.msisdns.forEach((msisdn) => {
                this.getUserDetails(msisdn).then((user) => {
                    switch (user.Type) {
                        case UserType.CARTRACKER:
                        case UserType.ROBOT:
                        case UserType.EXTERNAL_VIDEO_DESTINATION:
                        case UserType.EXTERNAL_VIDEO_SOURCE:
                            if (transmissionRequiredActions.includes(action)) {
                                Log.notice(
                                    'CONTACTS STORE',
                                    `The user ${msisdn} is not qualified for the action ${action} because is not human type`,
                                );

                                badUsers[msisdn] = user;
                            }
                            break;

                        default:
                            // all ok
                            break;
                    }
                });
            });

            data.groupsIds.forEach((groupId) => {
                this.getGroupDetails(groupId).then((group) => {
                    if (action === ContactMenuOption.ADD_TO_FAVORITES && groupId === ALL_GROUP_ID) {
                        Log.notice(
                            'CONTACTS STORE',
                            `The group ${groupId} is not qualified for the action ${action} because it is the ALL group`,
                        );
                        badGroups[group.GroupId] = group;
                    }
                    if (
                        transmissionRequiredActions.includes(action) && this.groupHasTransmissionDenied(group.GroupId)
                    ) {
                        Log.notice(
                            'CONTACTS STORE',
                            `The group ${groupId} is not qualified for the action ${action} because has transmission denied`,
                        );

                        badGroups[group.GroupId] = group;

                        // there was a single group, and is bad
                        if (data.msisdns.length === 0 && data.groupsIds.length === 1) {
                            errorToasterShown = true;

                            addToaster({
                                type: 'error',
                                message: t('js_all_not_allowed_to_talk'),
                            });

                            return;
                        }
                    }
                });
            });

            setTimeout(() => {
                if (errorToasterShown) {
                    resolve(false);

                    return;
                }

                const badMsisdns = Object.keys(badUsers);
                const badGroupsIds = Object.keys(badGroups).map((e) => parseInt(e, 10));

                if (badMsisdns.length === 0 && badGroupsIds.length === 0) {
                    resolve(true);

                    return;
                }

                if (badMsisdns.length + badGroupsIds.length === data.msisdns.length + data.groupsIds.length) {
                    // no contact ok
                    addConfirmationDialog({
                        question: t('js_all_contacts_invalid_contacts_for_action_all'),
                        yesCallback: () => {
                            // reset current selection
                            this.updateContactsSelectorData(contactsSelectorUuid, {
                                msisdns: [],
                                groupsIds: [],
                            });
                            resolve(false);
                        },
                        noCallback: () => {
                            resolve(false);
                        },
                        closeCallback: () => false,
                    });

                    return;
                }

                const names = [];

                badMsisdns.forEach((msisdn) => {
                    names.push(badUsers[msisdn].ComputedUserName);
                });

                badGroupsIds.forEach((groupId) => {
                    names.push(badGroups[groupId].Name);
                });

                // do NOT use HTML tags in stores, unless there is no other option, like this case
                // https://jira.streamwide.com/browse/SMARTMS-53333
                addConfirmationDialog({
                    question: t('js_all_contacts_invalid_contacts_for_action_some').replace(
                        '%contacts',
                        `<br /><br /><strong>${names.sort().join('<br>').trim()}</strong><br /><br />`,
                    ),
                    yesCallback: () => {
                        // remove the contacts from the selection
                        const okUsers = data.msisdns.filter((msisdn) => !badMsisdns.includes(msisdn));

                        const okGroups = data.groupsIds.filter((groupId) => !badGroupsIds.includes(groupId));

                        const newData = {
                            msisdns: okUsers,
                            groupsIds: okGroups,
                        };

                        this.updateContactsSelectorData(contactsSelectorUuid, newData);

                        // do not allow a single user in multiple select actions
                        if (okGroups.length === 0 && okUsers.length === 1) {
                            resolve(false);

                            return;
                        }

                        resolve(true);
                    },
                    noCallback: () => resolve(false),
                    closeCallback: () => false,
                });
            });
        });

    /**
     * Validate the action which should be called against a single contact.
     * @param {CugContact} contact The contact to call the action on
     * @param {ContactMenuOption} action The action to be executed
     * @return {boolean} Whether the action can be called or not
     */
    validateSingleContactAction = (contact, action) => {
        const transmissionRequiredActions = [
            ContactMenuOption.GROUP_CONVERSATION,
            ContactMenuOption.PTT_CALL,
            ContactMenuOption.BROADCAST_MESSAGE,
            ContactMenuOption.BROADCAST_CALL,
            ContactMenuOption.CONFERENCE_CALL,
            ContactMenuOption.SHARE_SCREEN,
            ContactMenuOption.BROADCAST_VIDEO,
            ContactMenuOption.VOIP_CALL,
            ContactMenuOption.PBX_CALL,
        ];

        if (!contact.IsGroup) {
            switch (contact.Type) {
                case UserType.CARTRACKER:
                case UserType.ROBOT:
                case UserType.EXTERNAL_VIDEO_DESTINATION:
                case UserType.EXTERNAL_VIDEO_SOURCE:
                    if (transmissionRequiredActions.includes(action) || action === ContactMenuOption.MESSAGE) {
                        Log.notice(
                            'CONTACTS STORE',
                            `The user ${contact.Msisdn} is not qualified for the action ${action} because is not human type`,
                        );
                        return false;
                    }
                    break;

                default:
                    // all ok
                    break;
            }
        } else if (
            transmissionRequiredActions.includes(action) && this.groupHasTransmissionDenied(contact.GroupId)
        ) {
            Log.notice(
                'CONTACTS STORE',
                `The group ${contact.GroupId} is not qualified for the action ${action} because it has transmission denied`,
            );
            return false;
        }

        return true;
    };

    logout = () => {
        // the logaut is handled at Legacy level; here will just trigger an event (for now)
        LocalEvent.trigger(LocalEvent.eventTypes.LOGOUT_REQUESTED);
    };

    /**
     * Sort array of msisdns according to the sorting rules from the Contacts page.
     * @param {string[]} msisdnsList
     * @return string[]
     */
    sortMsisdnsList = (msisdnsList) =>
        this.getOwnState().sortedUsersList.filter((msisdn) => msisdnsList.includes(msisdn));

    /**
     * Sort array of groupsIUds according to the sorting rules from the Contacts page.
     * @param {string[]} groupsIdsList
     * @return string[]
     */
    sortGroupsIdsList = (groupsIdsList) =>
        this.getOwnState().sortedGroupsList.filter((groupId) => groupsIdsList.includes(groupId));

    /**
     * Update the store data related with the contacts page.
     * @param {ContactsPageData} data
     */
    updateContactsPageData = (data) => {

        if(
            typeof data.globalAction !== 'undefined'
            && [
                ContactMenuOption.DIALER, ContactMenuOption.INVITE_GUEST
            ].includes(data.globalAction)
        ) {
            Log.debug(
                'CONTACTS STORE',
                `Executing global action ${data.globalAction} directly because it requires no contacts selection.`
            );

            switch (data.globalAction) {
                case ContactMenuOption.DIALER:
                    openPbxDialer();
                    break;

                case ContactMenuOption.INVITE_GUEST:
                    openGuestInvitationModal();
                    break;

                default:
                    // never reach here
                    break;
            }
        } else {
            this.dispatch('setContactsPageData', data);
        }
    };

    /**
     * Turn ON/OFF the PBX dialer.
     * @param {Function} callback
     */
    openPbxDialer = (callback = null) => this.dispatch('setPbxDialerData', { visible: true, callback });

    closePbxDialer = () => this.dispatch('setPbxDialerData', { visible: false, callback: null });

    /**
     * Retrieve the last called Msisdn
     * @return Promise<string>
     */
    getLastCalledNumber = () =>
        new Promise((resolve) => {
            webchatSDK.STWVoipCallManager.getLastCalledNumber()
                .then((msisdn) => {
                    resolve(msisdn);
                })
                .catch((e) => {
                    Log.error(
                        'CONTACTS STORE',
                        `Error while loading the last dialed number in PBX: ${JSON.stringify(e)}`,
                    );

                    resolve(null);
                });
        });

    /**
     * Open the details of a user when the msisdn is known.
     * This is useful for the case when the users were received in CUG by legacy, but they were not yet requested locally.
     * The function will request the user details if the user is not in the local cache.
     * @param {string} msisdn
     */
    openUserDetails = (msisdn) => {
        this.getUserDetails(msisdn, true).then((user) => {
            if (user === null) {
                Log.error('CONTACTS STORE', `No data returned for user ${msisdn}.`);

                return;
            }

            this.openContactDetails(msisdn, false, user);
        });
    };

    /**
     * Open the details of a contact (user or group).
     * NOTE: The function will not check if that contact is already open or not.
     * @param {string|number} contactId The Msisdn of a user or GroupId of a group.
     * @param {boolean} fromOtherDetailsPage The details are open from outside (will reset the chain),
     * or is open from the details page of another contact (Ex: a subscriber from under a group).
     */
    openContactDetails = (contactId, fromOtherDetailsPage = false, cachedContact = null) => {
        let contact = null;

        if (cachedContact === null) {
            contact = this.getUser(contactId);

            if (contact === null) {
                contact = this.getGroup(contactId);
            }

            if (contact === null) {
                Log.error(
                    'CONTACTS STORE',
                    `Cannot open details for id ${contactId} because the contact was not found in local cache.`,
                );

                return;
            }
        }

        this.dispatch('setDetailsLoadingContact', contact);

        /**
         * @param {CugContact|null} requestedContact
         */
        const resolver = (requestedContact) => {
            this.dispatch('setDetailsLoadingContact', null);

            if (requestedContact === null) {
                // something went wrong; the contact could not be retrieved or was deleted meanwhile
                Log.notice('CONTACTS STORE', `Empty details received for contact ${contact.ContactId}`);

                addToaster({
                    type: 'error',
                    message: t('js_all_cug_v3_error_generic'),
                });
            } else {
                let newChain = [];
                let newIndex = 0;
                if (fromOtherDetailsPage) {
                    // advance to the opened contact
                    newChain = [...this.getOwnState().detailsChain, requestedContact];
                    newIndex = newChain.length - 1;
                } else {
                    newChain = [requestedContact];
                }

                this.dispatch('updateDetailsChain', newChain);
                this.dispatch('updateDetailsIndex', newIndex);
            }
        };

        // no need another request; the contact was loaded and validated
        if (cachedContact) {
            resolver(cachedContact);

            return;
        }

        if (typeof contact.Msisdn !== 'undefined') {
            this.getUserDetails(contact.Msisdn, true).then((user) => {
                resolver(user);
            });
        } else {
            this.getGroupDetails(contact.GroupId, true, true).then((group) => resolver(group));
        }
    };

    closeContactDetails = () => {
        this.dispatch('updateDetailsChain', []);
    };

    refreshDetailsChain = () => {
        const state = this.getOwnState();
        const detailsChain = state.detailsChain;
        const detailsIndex = state.detailsIndex;

        if (detailsChain.length === 0) {
            return;
        }

        Log.notice('CONTACTS STORE', `Refreshing details chain upon CUG update`);

        const removedIndexes = [];
        const processedIds = [];
        const promises = [];

        detailsChain.forEach(
            /**
             * @param {CugContact} contactWithDetails
             */
            (contactWithDetails, index) => {
                if (processedIds.includes(contactWithDetails.ContactId)) {
                    return;
                }

                processedIds.push(contactWithDetails.ContactId);

                if (!contactWithDetails.IsGroup) {
                    // check if the user still exists
                    if (this.getUser(contactWithDetails.Msisdn) === null) {
                        removedIndexes.push(index);
                        return;
                    }

                    promises.push(this.getUserDetails(contactWithDetails.Msisdn, true));
                } else {
                    // check if the group still exists
                    if (this.getGroup(contactWithDetails.GroupId) === null) {
                        Log.debug(
                            'CONTACTS STORE',
                            `The contact ${contactWithDetails.ContactId} will be removed from details chain by CUG update`,
                        );

                        removedIndexes.push(index);

                        return;
                    }

                    promises.push(this.getGroupDetails(contactWithDetails.GroupId, true, true));
                }
            },
        );

        const newChain = [];

        this.dispatch('updateDetailsRefreshing', true);

        Promise.all(promises)
            .then(
                /**
                 * @param {CugContact[]} updatedContacts
                 */
                (updatedContacts) => {
                    updatedContacts.forEach((updatedContact) => {
                        newChain.push(updatedContact);

                        Log.debug('CONTACTS STORE', `Refreshed the details of contact ${updatedContact.ContactId}`);
                    });
                },
            )
            .finally(() => {
                this.dispatch('updateDetailsChain', newChain);
                this.dispatch('updateDetailsRefreshing', false);

                if (newChain.length === 0 || removedIndexes.includes(detailsIndex)) {
                    Log.notice('CONTACTS STORE', `The details index has been reset by CUG update`);
                    this.dispatch('updateDetailsIndex', 0);
                }
            });
    };

    /**
     * Go back in the details chain.
     */
    backInDetailsChain = () => {
        const state = this.getOwnState();
        const currentDetailsIndex = state.detailsIndex;
        const currentChain = state.detailsChain;
        // when going back, every thing is after the current index must be discarded
        const newChain = currentChain.filter((detail, index) => index < currentDetailsIndex);

        this.dispatch('updateDetailsIndex', currentDetailsIndex - 1);
        this.dispatch('updateDetailsChain', newChain);
    };

    /**
     * Get the list of favorite contacts.
     * @return {Promise<ContactsIdsList>}
     */
    getFavoriteContacts = () => this.searchContacts('both', 0, 1000, { onlyFavorites: true });

    refreshAllContactsSelectors = () => {
        // next step is to refresh the pages of the existing contacts selectors for users that may hev been added
        Object.keys(this.getOwnState().contactsSelectors).forEach((uuid) => {
            this.updateContactsSelectorData(uuid, { refreshRequired: true });
        });
    };

    /**
     * This function is necessary to be called whe CUG is changing the company structure, or the users properties
     */
    refreshAllLoadedUsers = () => {
        const state = this.getOwnState();
        const recordsOnPage = contactsRecordsOnPage();

        // load the users in batches
        const startTime = new Date().getTime();
        const msisdns = Object.keys(state.users);
        const receivedMsisdns = [];

        Log.notice('CONTACTS STORE', `Refreshing ${msisdns.length} users upon CUG update`);

        const processReceivedUsers = (usersData) => {
            const receivedUsers = [];
            usersData.data.results.forEach((userData) => {
                const user = createUserStructure(userData);
                receivedMsisdns.push(user.Msisdn);
                receivedUsers.push(user);
            });

            this.dispatch('setMultipleUserData', receivedUsers);
        };

        const queue = new Queue(() => {
            Log.debug('CONTACTS STORE', `Refreshed users upon CUG update in ${new Date().getTime() - startTime}ms`);
        });

        if (msisdns.length <= recordsOnPage) {
            queue.add(() => {
                webchatSDK.STWAPI.findSubscribersByMsisdnList(msisdns, false).then((usersData) => {
                    processReceivedUsers(usersData);
                    queue.advance();
                });
            });
        } else {
            for (let n = 0; n < msisdns.length; n += recordsOnPage) {
                queue.add(() => {
                    webchatSDK.STWAPI.findSubscribersByMsisdnList(msisdns.slice(n, n + recordsOnPage), false)
                        .then((usersData) => {
                            processReceivedUsers(usersData);
                            queue.advance();
                        })
                        .finally(() => queue.advance());
                });
            }
        }

        queue.run();
    };

    /**
     * This function is necessary to be called whe CUG is changing the company structure, or the users properties
     */
    refreshAllLoadedGroups = () => {
        const state = this.getOwnState();

        Log.notice('CONTACTS STORE', `Refreshing ${Object.keys(state.groups).length} groups upon CUG update`);

        const startTime = new Date().getTime();
        const queue = new Queue(() => {
            Log.debug('CONTACTS STORE', `Refreshed groups is ${new Date().getTime() - startTime}ms`);
        });

        Object.keys(state.groups).forEach((groupId) => {
            queue.add(() => {
                this.getGroupDetails(groupId, false, true).finally(() => queue.advance());
            });
        });

        queue.run();
    };

    /**
     * Get the picture of a user.
     * @param {string} msisdn
     * @return {string|null}
     */
    getOrLoadUserPicture = (msisdn) => {
        const ownState = this.getOwnState();
        const user = this.getUser(msisdn);

        // user unknown yet
        if (user === null) {
            if (typeof ownState.userPictures[msisdn] !== 'undefined') {
                Log.debug(
                    'CONTACTS STORE',
                    `Former user picture of the user with msisdn "${msisdn}" has been removed.`,
                );

                this.dispatch('removeUserPicture', msisdn);
            }

            return null;
        }

        if (user.PictureTimestamp === 0) {
            return null;
        }

        if (typeof this.pendingUserPicturesRequests[msisdn] !== 'undefined') {
            return null;
        }

        // picture OK
        if (
            typeof ownState.userPictures[msisdn] !== 'undefined' &&
            ownState.userPictures[msisdn].pictureTimestamp === user.PictureTimestamp
        ) {
            return ownState.userPictures[msisdn].picture;
        }

        Log.debug('CONTACTS STORE', `Requesting user picture for "${msisdn}"`);

        this.pendingUserPicturesRequests[msisdn] = true;
        sdkClient.emitSocketEvent('WCFUI_USER_PICTURE_REQUEST', { msisdn });

        return null;
    };

    /**
     * @param {{msisdn: string, mimeType: string|null, content: string|null}} pictureData
     */
    onUserPictureResponse = (pictureData) => {
        const user = this.getUser(pictureData.msisdn);

        if (user === null) {
            Log.notice(
                'CONTACTS STORE',
                `A picture has been received fro msisdn ${pictureData.msisdn}, but the user is no longer loaded.`,
            );

            this.dispatch('removeUserPicture', pictureData.msisdn);
            delete this.pendingUserPicturesRequests[pictureData.msisdn];

            return;
        }

        if (pictureData.mimeType === null) {
            this.dispatch('removeUserPicture', pictureData.msisdn);
        } else {
            this.dispatch('setUserPicture', {
                msisdn: pictureData.msisdn,
                picture: `data:${pictureData.mimeType};base64, ${pictureData.content}`,
                pictureTimestamp: user.PictureTimestamp,
            });
        }

        delete this.pendingUserPicturesRequests[pictureData.msisdn];
    };

    /**
     * @param {CugSubscriber} user
     * @return {string|null}
     */
    getUserDefaultGlyphIcon = (user) => {
        if (user === null) {
            return null;
        }

        let defaultIcon = null;

        switch (user.Type) {
            case UserType.USER:
            case UserType.THREE_GPP:
                // these will have their initial displayed
                break;

            case UserType.CARTRACKER:
                defaultIcon = 'stw-car-tracker';
                break;

            case UserType.EXTERNAL_VIDEO_DESTINATION:
            case UserType.EXTERNAL_VIDEO_SOURCE:
                defaultIcon = 'stw-external-video-source';

            case UserType.ROBOT:
                if (user.RobotType === RobotType.ROBOT_MY_RULES) {
                    defaultIcon = 'stw-bot';
                }

                if (user.RobotType === RobotType.ROBOT_SYSTEM) {
                    defaultIcon = 'stw-bot';
                }
                break;

            default:
                // nothing to do
                break;
        }

        return defaultIcon;
    };

    /**
     * Get the groups which are linked with the given msisdn.
     * @param {string} msisdn
     * @return {Promise<CugGetContact>}
     */
    cugGetContact = (msisdn) =>
        new Promise((resolve) => {
            webchatSDK.STWAPI.cugGetContact(msisdn)
                .then((rawContactData) => {
                    const contactData = rawContactData.data.results.ContactInfo.Subscriber[0];

                    /**
                     * @type {CugGetContact}
                     */
                    const result = createUserStructure(contactData);
                    result.GroupsIds = contactData.Groups;
                    result.GroupsDetails = contactData.GroupsDetails;

                    resolve(result);
                })
                .catch((e) => {
                    Log.error(
                        'CONTACTS STORE',
                        `There was an error while loding CugGetContact for msisdn ${msisdn}: ${e}`,
                    );

                    resolve(null);
                });
        });

    /**
     * Trigger first action for a given contact.
     * @param {CugContact} contact The contact for whom to trigger the first action
     * @param {ContactMenuOption} menuOption The action to be executed
     * @param {ContactMenuType} menuType From which menu the action was triggered
     * @return {null}
     */
    triggerFirstAction = (contact, menuOption, menuType = ContactMenuType.CONTACTS_PAGE) => {
        if (this.validateSingleContactAction(contact, menuOption)) {
            this.triggerMenuOption(contact, menuOption, menuType);
        } else {
            Log.notice(
                'CONTACTS STORE',
                `The default action ${menuOption} cannot be executed on contact ${contact.ContactId}. Will open details instead.`,
            );
            this.openContactDetails(contact.ContactId, true);
        }
    };
}

// the handler
export const contactsSliceHandler = ContactsSliceHandler.getInstance();
// the selectors to be used in useSelect
export const selectLoggedInUser = (state) => state.contactsSlice.users[wcfUiCfg.msisdn];
export const selectContactsPageData = (state) => state.contactsSlice.contactsPageData;
export const selectFavoriteContactsData = (state) => state.contactsSlice.favoritesContactsData;
export const selectUsersListFilters = (state) => state.contactsSlice.usersListFilters;
export const selectPbxDialerData = (state) => state.contactsSlice.pbxDialerData;
export const selectDetailsChain = (state) => state.contactsSlice.detailsChain;
export const selectDetailsIndex = (state) => state.contactsSlice.detailsIndex;
export const selectDetailsRefreshing = (state) => state.contactsSlice.detailsRefreshing;
export const selectDetailsLoadingContact = (state) => state.contactsSlice.detailsLoadingContact;

export const {
    logout,
    getLoggedInUser,
    setContactsFilters,
    registerSearchInput,
    unregisterSearchInput,
    getSearchInputData,
    updateSearchInputData,
    requestSearchInputPage,
    registerContactsSelector,
    unregisterContactsSelector,
    contactsSelectorIsRegistered,
    getContactsSelectorData,
    updateContactsSelectorData,
    resetContactsSelectorData,
    getOrLoadUserPicture,
    getUserDetails,
    getOrLoadUserDetails,
    getUser,
    searchContacts,
    getGroupDetails,
    getOrLoadGroupDetails,
    getGroup,
    toggleFavoriteStatus,
    computeMenuOptions,
    triggerMenuOption,
    execContactsClientAction,
    sortMsisdnsList,
    sortGroupsIdsList,
    groupHasTransmissionDenied,
    updateContactsPageData,
    getLastCalledNumber,
    openPbxDialer,
    closePbxDialer,
    openContactDetails,
    openUserDetails,
    closeContactDetails,
    backInDetailsChain,
    getFavoriteContacts,
    getContact,
    groupIsMember,
    getUserDefaultGlyphIcon,
    groupIsAdmin,
    triggerFirstAction,
    findUsersByMsisdnList,
    findGroupsByIdsList,
} = contactsSliceHandler;
